import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  Subscription,
  firstValueFrom,
  fromEvent,
  merge,
  throwError,
} from 'rxjs'
import { filter, take, timeout, catchError, map } from 'rxjs/operators'
import { SpjsStandCentral } from '@/ble/infrastructures/spjsStandCentral'
import { StandPeripheral } from '@/ble/interfaces/standPeripheral'
import { BleProtocolResolver } from '@/ble/resolver/bleProtocolResolver'
import { matchesStandId } from '@/ble/infrastructures/scanMatching'
import {
  WRITE_WITHOUT_RESPONSE,
  WRITE_WITH_RESPONSE,
} from '@/ble/infrastructures/spjsStandPeripheral'
import {
  SCAN_TIMEOUT_MS,
  BATTERY_TIMEOUT_MS,
} from '@/ble/infrastructures/bleConfig'
import { ScanTimeoutException } from '@/ble/errors/scanTimeout'
import { BluetoothCancelException } from '@/ble/errors/bluetoothCancel'
import { BluetoothNotOnException } from '@/ble/errors/bluetoothNotOn'
import { BleConnectionPhase } from '@/ble/entities/bleConnectionPhase'
import { BleAnalyticsEvent } from '@/ble/entities/bleAnalyticsEvent'
import { FirmwareCapabilities } from '@/ble/entities/firmwareCapabilities'
import { StandProtocol } from '@/ble/entities/standProtocol'
import { StandOperationResult } from '@/ble/entities/standOperationResult'
import {
  bleBluetoothNotOn,
  bleScanTimeout,
  bleUnknownFailure,
  BleFailureReason,
} from '@/ble/entities/bleFailureReason'
import { V14TimeoutController } from '@/ble/protocols/crc32AuthV14/v14TimeoutController'
import { Crc32AuthOperationResultAdapter } from '@/ble/protocols/crc32AuthV14/crc32AuthOperationResultAdapter'
import { Crc32AuthPacketBuilder } from '@/ble/protocols/crc32AuthV14/crc32AuthPacketBuilder'
import { AesOperationResultAdapter } from '@/ble/protocols/aes/aesOperationResultAdapter'
import { buildAesPacket, readAesResponse } from '@/ble/protocols/aes/aesPacket'
import { AesCommand } from '@/ble/protocols/aes/aesTypes'
import {
  wireV14ResultStream,
  wireAesResultStream,
  isWellFormedAesFrame,
  AES_BATTERY_ECHO,
} from './resultStreamWiring'
import { parseStandPowerData } from './parseStandPowerData'

type Session = { protocol: StandProtocol; capabilities: FirmwareCapabilities }

// writeFailed analytics の order（操作種別・platform順）。レンタル=0 / 返却=1。
const WRITE_ORDER = { takeOut: 0, putIn: 1 } as const

function wrapError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e))
}

// 接続lifecycleの中核。3ストリーム（phase/analytics=安定・connect跨ぎで購読を維持できる／
// operationResult=connectごとに新規Subjectを張り直す）を公開し、connect/disconnectで
// scan(adapter open 内包)→establish→resolveのlifecycleを一本化する。
// notify配線(wireNotify)とprotocol分岐writeを含む接続lifecycleの中核。
export class StandOperationRepository {
  private readonly central: SpjsStandCentral
  private readonly resolver: BleProtocolResolver
  private readonly phaseSubject = new BehaviorSubject<BleConnectionPhase>({
    type: 'idle',
  })
  private readonly analyticsSubject = new Subject<BleAnalyticsEvent>()

  private session: Session | null = null
  private peripheral: StandPeripheral | null = null
  private operationResultSubject: Subject<StandOperationResult> | null = null
  private connectionSub: Subscription | null = null
  private timeoutController: V14TimeoutController | null = null
  private readonly crc32AuthAdapter = new Crc32AuthOperationResultAdapter()
  private readonly aesAdapter = new AesOperationResultAdapter()
  private readonly packetBuilder = new Crc32AuthPacketBuilder()
  private readonly now: () => number

  constructor(options: {
    central: SpjsStandCentral
    resolver?: BleProtocolResolver
    now?: () => number
  }) {
    this.central = options.central
    this.resolver = options.resolver ?? new BleProtocolResolver()
    // AES の randoms は「秒 + 901214048 のオフセット」時刻由来（`src/utils/bluetooth.ts` の
    // generateValue と同一形式）。スタンドがこの時刻形式を検証するため、生の Date.now()（ミリ秒）
    // を渡すとコマンドを拒否される。V14 は別 builder（Crc32AuthPacketBuilder）で now() 非依存。
    this.now = options.now ?? (() => Math.floor(Date.now() / 1000) + 901214048)
  }

  get observeConnectionPhase$(): Observable<BleConnectionPhase> {
    return this.phaseSubject.asObservable()
  }
  get observeAnalyticsEvents$(): Observable<BleAnalyticsEvent> {
    return this.analyticsSubject.asObservable()
  }
  get observeOperationResult$(): Observable<StandOperationResult> {
    if (!this.operationResultSubject) {
      throw new Error('not connected: observeOperationResult$ is unavailable')
    }
    return this.operationResultSubject.asObservable()
  }
  get capabilities(): FirmwareCapabilities {
    if (!this.session)
      throw new Error('not connected: capabilities is unavailable')
    return this.session.capabilities
  }

  get protocol(): StandProtocol {
    if (!this.session) throw new Error('not connected: protocol is unavailable')
    return this.session.protocol
  }

  // 接続以降のnotify受信回数。StandOperationPort越しにstandServiceがwrite前後の
  // スナップショット差分として消費する(sawAnyFrameの材料)。peripheral の
  // rawNotifyCount(デコード前)へ委譲する。自前で数えると decodePacket 失敗フレームを
  // 取りこぼすため、repository は自前カウンタを持たない。
  // 未接続時は0(再接続時は新peripheralインスタンスで自然に0リセットされる契約と等価)。
  get notifyFrameCount(): number {
    return this.peripheral?.rawNotifyCount ?? 0
  }

  // discovery後の識別情報からwriteTypeを決める。プロトコル知識(V14=without-response /
  // 旧AES=with-response)はここに閉じ、central層は非依存のまま既定値のみ持つ。
  private writeTypeFor = (info: {
    advertisedName: string | null
    serviceUuid: string
  }): number =>
    this.resolver.resolve(info).protocol === 'crc32AuthV14'
      ? WRITE_WITHOUT_RESPONSE
      : WRITE_WITH_RESPONSE

  private classifyFailure(e: unknown): BleFailureReason {
    if (e instanceof BluetoothNotOnException) {
      return bleBluetoothNotOn()
    }
    if (e instanceof ScanTimeoutException) return bleScanTimeout()
    return bleUnknownFailure(e)
  }

  // signal は「中断 → BluetoothCancelException」の producer（aikasa/ikasa_platform#7595）。
  // abort検知（before connect / scan待ち中 / establish待ち中）はすべて下のcatchへ
  // 合流し、既存の cancel silent-return（failed非発・connectionAttemptFailed非発）を再利用する。
  async connect(
    standId: string,
    opts?: { signal?: AbortSignal }
  ): Promise<StandPeripheral | null> {
    const signal = opts?.signal
    try {
      // 旧接続の後始末はtry内で行う。再connect時に旧p.disconnect()がrejectしても
      // 下のcatch（connectionAttemptFailed→failed→idle→rethrow）で一貫して処理され、
      // phaseがstale('ready')のまま残らない。新規connectではpriorDisconnect===undefined
      // なのでawaitせず、happy pathのtick数は不変。
      const priorDisconnect = this.clearOperationState({
        disconnectCentral: true,
      })
      if (priorDisconnect) await priorDisconnect
      if (signal?.aborted)
        throw new BluetoothCancelException('aborted before connect')
      // adapter open は central.scan$ 購読に内包される（aikasa/ikasa_platform#7681）。
      // open 失敗（BT off）は scan$ が BluetoothNotOnException で error 終端し、下の catch の
      // classifyFailure（instanceof 分類）へ既存経路で合流する。上の aborted チェックから
      // firstValueFrom の購読までは await を挟まないため、「購読前 abort」の競合窓は無い
      //（open の await 中の abort は live な abort$（fromEvent）が拾う）。
      this.phaseSubject.next({ type: 'scanning' })
      // abort$: signal の 'abort' で BluetoothCancelException を投げるstream。scan待ち中に
      // abortが起きた場合、mergeによりabort$がscan$に競り勝ち、この例外がcatchErrorを透過して
      // 下のcatchへ届く（TimeoutError以外はそのまま透過する既存挙動を利用）。
      const abort$ = signal
        ? fromEvent(signal, 'abort').pipe(
            map(() => {
              throw new BluetoothCancelException('aborted during scan')
            })
          )
        : EMPTY
      const device = await firstValueFrom(
        merge(
          this.central
            .scan$()
            .pipe(filter((d) => matchesStandId(d.name, standId))),
          abort$
        ).pipe(
          take(1),
          timeout({ each: SCAN_TIMEOUT_MS }),
          catchError((e: any) =>
            throwError(() =>
              e?.name === 'TimeoutError' ? new ScanTimeoutException() : e
            )
          )
        )
      )
      if (signal?.aborted)
        throw new BluetoothCancelException('aborted after scan')
      this.phaseSubject.next({ type: 'establishing' })
      const peripheral = await this.central.connect(device, {
        writeTypeFor: this.writeTypeFor,
      })
      // abortチェックより先にthis.peripheralへ代入する。先にthrowすると
      // 確立済みperipheralがローカル変数のまま到達不能になり、下のcatchのclearOperationState
      // （this.peripheral参照）が何も切断できず接続がリークする（スタンド専有・再試行不能）。
      this.peripheral = peripheral
      // establishのawait中にabortされた場合もcancelとして正規化する。
      // 下のcatchがsilent-returnと後始末を担う（connectionAttemptFailedを出さない・
      // this.peripheralは上で代入済みのためdisconnectまで到達する）。
      if (signal?.aborted)
        throw new BluetoothCancelException('aborted during establish')
      this.session = this.resolver.resolve({
        advertisedName: peripheral.advertisedName,
        serviceUuid: peripheral.serviceUuid,
      })
      this.operationResultSubject = new Subject<StandOperationResult>()
      this.connectionSub = new Subscription()
      this.wireNotify(peripheral, this.session)
      this.phaseSubject.next({
        type: 'ready',
        capabilities: this.session.capabilities,
      })
      this.analyticsSubject.next({ type: 'connectCompleted', standId })
      return peripheral
    } catch (e) {
      if (e instanceof BluetoothCancelException) {
        const cleared = this.clearOperationState({
          disconnectCentral: true,
          closeAdapterWhenNoPeripheral: true,
        })
        // close の完了を待ってから返る契約（aikasa/ikasa_platform#7681 レビュー回収）:
        // 先に返ると直後の再試行の open と前操作の close が交差し得る（順序自体は
        // central の直列化キューが守るが、契約としてもここで畳み切る）。
        // 切断失敗は cancel の silent-return を壊さないよう swallow。
        if (cleared) await cleared.catch(() => undefined)
        this.phaseSubject.next({ type: 'idle' })
        return null
      }
      const attemptPhase =
        this.phaseSubject.value.type === 'scanning'
          ? ({ type: 'scan' } as const)
          : ({ type: 'connect' } as const)
      this.analyticsSubject.next({
        type: 'connectionAttemptFailed',
        standId,
        phase: attemptPhase,
        error: wrapError(e),
      })
      this.phaseSubject.next({
        type: 'failed',
        reason: this.classifyFailure(e),
      })
      const cleared = this.clearOperationState({
        disconnectCentral: true,
        closeAdapterWhenNoPeripheral: true,
      })
      // close 完了を待ってから rethrow（上の cancel 経路と同じ契約）。
      // 切断失敗は二次的なので swallow し、意味のある元エラーを優先 surface する。
      if (cleared) await cleared.catch(() => undefined)
      this.phaseSubject.next({ type: 'idle' })
      throw e
    }
  }

  // notify$をprotocolに応じてV14/AESの結果ストリームへ配線し、operationResultSubjectへ流す。
  // 購読・teardown（V14のtimeoutController破棄含む）はすべて②Subscription bag(connectionSub)へ
  // 積み、disconnect時のunsubscribeで一括後始末する。
  private wireNotify(peripheral: StandPeripheral, session: Session): void {
    const sink = this.operationResultSubject!
    const sub = this.connectionSub!
    if (session.protocol === 'crc32AuthV14') {
      const controller = new V14TimeoutController()
      this.timeoutController = controller
      const { results$, feedSub } = wireV14ResultStream(
        peripheral.notify$,
        controller,
        this.crc32AuthAdapter
      )
      sub.add(feedSub)
      sub.add(
        results$.subscribe({
          next: (r) => sink.next(r),
          error: (e) => {
            this.analyticsSubject.next({
              type: 'responseStreamFailed',
              error: wrapError(e),
            })
            sink.error(e)
          },
        })
      )
      sub.add(() => {
        this.timeoutController?.onDisconnect()
        this.timeoutController = null
      })
    } else {
      const stream$ = wireAesResultStream(
        peripheral.notify$,
        session.capabilities.canDistinguishReaders,
        this.aesAdapter,
        (e) =>
          this.analyticsSubject.next({
            type: 'responseStreamFailed',
            error: wrapError(e),
          }) // ★malformedはanalytics通知のみ・非終端(sink.errorしない)
      )
      sub.add(stream$.subscribe((r) => sink.next(r)))
    }
    sub.add(() => sink.complete())
  }

  // リンク生存確認 probe（#7709）。成功のみ true。未接続・失敗・cap 超過は false に畳む
  // （probe 失敗は「リンク不明」であり、呼び出し側は安全側=閉じない、に倒す前提）。
  async probeLinkAlive(): Promise<boolean> {
    const peripheral = this.peripheral
    if (peripheral === null) return false
    try {
      await peripheral.readRssi()
      return true
    } catch (e) {
      // repository は processId を知らないため、standService 側の終端 breadcrumb
      // (processId 付き)との突合材料として deviceId を添える。
      console.warn('[ble] link probe failed (treated as link-dead)', {
        deviceId: peripheral.deviceId,
        error: e,
      })
      return false
    }
  }

  async disconnect(): Promise<void> {
    if (this.phaseSubject.value.type !== 'idle') {
      this.phaseSubject.next({ type: 'disconnecting' })
    }
    try {
      await this.clearOperationState({
        disconnectCentral: true,
        closeAdapterWhenNoPeripheral: true,
      })
    } finally {
      this.phaseSubject.next({ type: 'idle' }) // close失敗でも phase は idle へ収束
    }
  }

  // レンタル(0x04 legacy / 0x0D modern) / V14(rentalパケット)の3分岐write。
  // (AES) write失敗時はwriteWithAnalyticsがanalytics→disconnectの順で後始末する。
  // (V14) writeV14 は発行時 resolve のためここでは失敗せず、ack timeout が受け皿。
  async writeTakeOut(): Promise<void> {
    return this.writeWithAnalytics(WRITE_ORDER.takeOut, async () => {
      const s = this.session!
      if (s.protocol === 'crc32AuthV14')
        return this.writeV14(this.packetBuilder.buildRentalPacket())
      if (!s.capabilities.canDistinguishReaders)
        return this.peripheral!.write(
          buildAesPacket(AesCommand.rentalLegacy, this.now())
        )
      return this.peripheral!.write(
        buildAesPacket(AesCommand.modern, this.now(), {
          isLegacyFirmware: false,
          isRental: true,
        })
      )
    })
  }

  // 返却(0x05 legacy / 0x0D modern) / V14(returnパケット)の3分岐write。
  async writePutIn(): Promise<void> {
    return this.writeWithAnalytics(WRITE_ORDER.putIn, async () => {
      const s = this.session!
      if (s.protocol === 'crc32AuthV14')
        return this.writeV14(this.packetBuilder.buildReturnPacket())
      if (!s.capabilities.canDistinguishReaders)
        return this.peripheral!.write(
          buildAesPacket(AesCommand.returnLegacy, this.now())
        )
      return this.peripheral!.write(
        buildAesPacket(AesCommand.modern, this.now(), {
          isLegacyFirmware: false,
        })
      )
    })
  }

  // AES専用の0x08独立往復。operation stream とは別に notify$ を購読し、echo=0x88 を1件だけ拾って
  // parseStandPowerData でバッテリー値を返す。operation 側(wireAesResultStream)は 0x88 のみを demux
  // 除外する棲み分けなので追加ロジックは不要。V14 は result frame にバッテリー同梱のため対象外。
  async readStandBattery(): Promise<number> {
    if (this.session?.protocol !== 'aes') {
      throw new Error(
        'readStandBattery is only valid for AES stands (V14 battery comes in the result frame)'
      )
    }
    const peripheral = this.peripheral!
    // 応答購読(subscribe)→write の順を保ち、両者を単一Promiseに構造化する。
    // これによりwrite失敗時に購読(15sタイマ)をunsubscribeして即rejectでき、
    // 応答promiseが宙吊り(未await・unhandledRejection)になる経路を構造的に排除する。
    const decoded = await new Promise<number[]>((resolve, reject) => {
      const sub = peripheral.notify$
        .pipe(
          // readAesResponseはlength検証しないため、ここ（消費側）で isWellFormedAesFrame により構造検証し、
          // 破損フレームがdecoded[0]偶然0x88になって誤電池値を返すのを防ぐ。
          // crc 観測は wireAesResultStream 側（demux 前）で既に全フレーム（電池フレーム含む）を
          // 観測しているため、ここでは重複させない。
          filter(isWellFormedAesFrame),
          map((packet) => readAesResponse(packet)),
          filter((d) => d[0] === AES_BATTERY_ECHO), // 0x08応答エコーだけ拾う
          take(1),
          timeout({ each: BATTERY_TIMEOUT_MS })
        )
        .subscribe({
          next: (d) => resolve(d),
          error: (e) => reject(e),
          // disconnect()→peripheral.disconnect()→notify$.complete() で in-flight read が
          // 応答なしのまま終端する経路。complete ハンドラが無いとtake(1)/timeoutは無emitで
          // 完了するだけでPromiseが確定せず、await が永久にハングする（★構造的排除の要）。
          complete: () =>
            reject(new Error('disconnected before battery response')),
        })
      // subscribe後にwrite（応答取りこぼし防止）。独立往復（rental/returnのwriteとは分離）。
      // write失敗時は購読をteardownして即reject（15sタイマ宙吊り＆unhandledRejection防止）。
      peripheral
        .write(buildAesPacket(AesCommand.readPower, this.now()))
        .catch((e) => {
          sub.unsubscribe()
          reject(e)
        })
    })
    const power = parseStandPowerData(decoded)
    if (!power.resultOK)
      throw new Error('stand power read failed (resultOK=false)')
    return power.battery
  }

  private async writeV14(packet: Uint8Array): Promise<void> {
    this.timeoutController!.onWriteIssued() // ★write より先（書込直後のack/resultを取りこぼさない）
    // V14 の write 特性(fff1)は write-without-response で、spjs ブリッジの write 返信は
    // 返らない/大幅に遅延する（実機で結果フレームより後になり、stage='wrote' が単調ガードに
    // 棄却されて手動完了ボタンの窓が一度も開かないバグの原因 = aikasa/ikasa_platform#7705）。
    // native(FlutterBluePlus withoutResponse:true) と同じく「発行時点で完了扱い」にする。
    // 書込の実失敗は ack timeout(3s)→result stream error が有界な受け皿（writeWithAnalytics の
    // catch / standService の write reject 側 settle は AES 専用になる）。
    void this.peripheral!.write(packet).catch((e) => {
      console.warn(
        '[ble] V14 write bridge error (ignored; ack timeout is the backstop)',
        e
      )
    })
  }

  // write error → analytics(writeFailed) → disconnect → rethrow（★analytics先）。
  // ※この catch は AES(with-response) 専用。V14 は writeV14 が発行時 resolve するため
  //   ここでは失敗を検知できず、ack timeout 経由で responseStreamFailed が出る（#7705）。
  // writeTakeOut/writePutIn共通のこの1点でoperating phaseをemitする。
  // StandServiceがbattery読取+wrote POSTの後にwrite thunkを呼ぶため、'operating'は
  // それらの完了後・実write直前を表す（旧UIの「解錠write直前にTaking」を再現する起点）。
  private async writeWithAnalytics(
    order: number,
    op: () => Promise<void>
  ): Promise<void> {
    try {
      this.phaseSubject.next({ type: 'operating' })
      await op()
    } catch (e) {
      this.analyticsSubject.next({
        type: 'writeFailed',
        order,
        error: wrapError(e),
      }) // ★disconnect より先
      try {
        await this.disconnect()
      } catch {
        // disconnect(close)失敗は二次的。意味のある元 write error を優先 surface する。
      }
      throw e
    }
  }

  // ②Subscription bag: connect単位のsubscription(notify配線等)をまとめてunsubscribeする。
  // peripheral.disconnect()がcentral切断(closeBLEConnection)とadapter closeの畳みまで行う。
  private clearOperationState(opts: {
    disconnectCentral: boolean
    // 終了経路専用の fallback（aikasa/ikasa_platform#7681）: peripheral 未生成の終了
    // （scan timeout / BT off / scan中cancel / establish失敗）でも adapter close を機構的に
    // 保証する。connect 冒頭の旧接続後始末では使わない — 直後に scan$ が再 open するため
    // close は旧 peripheral の畳みに任せ、close→open の競合窓と happy path の tick 増を避ける。
    closeAdapterWhenNoPeripheral?: boolean
  }): void | Promise<void> {
    this.session = null
    this.connectionSub?.unsubscribe()
    this.connectionSub = null
    this.operationResultSubject = null
    const p = this.peripheral
    this.peripheral = null
    if (opts.disconnectCentral && p) return p.disconnect()
    if (opts.disconnectCentral && opts.closeAdapterWhenNoPeripheral)
      // central.closeAdapter は直列化キュー経由（pending の open を追い越さない）で、
      // swallow+warn を内包する（reject しない契約）。
      return this.central.closeAdapter()
  }
}
